import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { displayList } from "@auto/widgets/ListComponent";
import ReportExcelExportButton from "@src/reporting/components/ReportExcelExportButton";
import {
  getLocale,
  rest,
  t
} from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import {
  TABLET_REPORTS_FACETS,
  buildTabletReportPayload,
  isDefaultTabletReportFilters,
  normalizeTabletReportFilters
} from "./TabletReportsFacets";

import styles from "./TabletReports.module.styl";

const DEFAULT_PAGE_SIZE = 50;

let currentExportState = {
  search: "",
  sort: [],
  filters: normalizeTabletReportFilters()
};

let initialRange = null;
let initialRangeUsesLeft = 0;

const readRangeFromUrl = () => {
  const params =
    new URLSearchParams(
      window.location.search
    );

  const offset =
    Number(params.get("offset"));

  const limit =
    Number(params.get("limit"));

  const safeLimit =
    Number.isFinite(limit) &&
    limit > 0
      ? Math.floor(limit)
      : DEFAULT_PAGE_SIZE;

  const safeOffset =
    Number.isFinite(offset) &&
    offset >= 0
      ? Math.floor(offset)
      : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page:
      Math.floor(
        safeOffset / safeLimit
      ),
    pageSize: safeLimit,
    hasExplicitRange:
      params.has("offset") ||
      params.has("limit")
  };
};

const writeRangeToUrl = ({
  offset,
  limit
}) => {
  const url =
    new URL(window.location.href);

  url.searchParams.set(
    "offset",
    String(offset)
  );

  url.searchParams.set(
    "limit",
    String(limit)
  );

  window.history.replaceState(
    {},
    "",
    url.toString()
  );
};

const getEffectiveQuery = (query) => {
  const search =
    String(
      query?.search || ""
    ).trim();

  const sort =
    Array.isArray(query?.sort)
      ? query.sort
      : [];

  const filters =
    normalizeTabletReportFilters(
      query?.filters
    );

  const hasActiveFilters =
    !isDefaultTabletReportFilters(
      filters
    );

  const page =
    Number.isFinite(query?.page)
      ? query.page
      : 0;

  const pageSize =
    Number.isFinite(query?.pageSize)
      ? query.pageSize
      : DEFAULT_PAGE_SIZE;

  if (
    initialRange &&
    initialRange.hasExplicitRange &&
    initialRangeUsesLeft > 0 &&
    !search &&
    !hasActiveFilters &&
    page === 0 &&
    pageSize === DEFAULT_PAGE_SIZE
  ) {
    initialRangeUsesLeft -= 1;

    return {
      page:
        initialRange.page,

      pageSize:
        initialRange.pageSize,

      offset:
        initialRange.offset,

      limit:
        initialRange.limit,

      search,
      sort,
      filters
    };
  }

  initialRangeUsesLeft = 0;

  return {
    page,
    pageSize,
    offset:
      page * pageSize,
    limit:
      pageSize,
    search,
    sort,
    filters
  };
};

const selectorsForSortItem = (
  sortItem
) => {
  const column =
    sortItem?.column;

  if (
    Array.isArray(
      column?.sortSelectors
    )
  ) {
    return column.sortSelectors;
  }

  if (column?.sortSelector) {
    return [
      column.sortSelector
    ];
  }

  if (sortItem?.field) {
    return [
      sortItem.field
    ];
  }

  return [];
};

const buildSortPayload = (sort) => {
  const result = [];

  for (
    const sortItem
    of sort || []
  ) {
    const direction =
      String(
        sortItem?.direction || ""
      ).toLowerCase();

    if (
      direction !== "asc" &&
      direction !== "desc"
    ) {
      continue;
    }

    for (
      const selector
      of selectorsForSortItem(
        sortItem
      )
    ) {
      if (selector) {
        result.push({
          selector,
          direction
        });
      }
    }
  }

  return result;
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    getLocale(),
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }
  ).format(date);
};

const accountStatusLabel = (value) => {
  const raw =
    String(value || "").trim();

  if (!raw) {
    return "";
  }

  const key = {
    ACTIVE: "common.active",
    DISABLED: "common.disabled",
    PENDING: "common.pending",
    APPROVED: "common.approved",
    REJECTED: "common.rejected",
    ERROR: "common.error"
  }[raw.toUpperCase()];

  return key
    ? t(key)
    : raw;
};

const operatorName = (row) => {
  const value = [
    row?.operatorFirstName,
    row?.operatorLastName
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    value ||
    row?.operatorUsername ||
    row?.operatorNationalId ||
    ""
  );
};

const operatorImageSrc = (row) => {
  if (
    !row?.operatorDefaultImageId
  ) {
    return "";
  }

  return (
    getServiceUri() +
    `person-photo/data/${Number(
      row.operatorDefaultImageId
    )}`
  );
};

const TabletCell = ({ row }) => {
  const identity =
    row.mdmNumber ||
    row.deviceId ||
    row.serial ||
    t("common.unknown");

  return (
    <div className={styles.tabletCell}>
      <div className={styles.tabletIdentity}>
        {identity}
      </div>

      <div className={styles.tabletMeta}>
        {row.model ? (
          <span>{row.model}</span>
        ) : null}

        {row.serial &&
        row.serial !== identity ? (
          <span className={styles.monoMeta}>
            {row.serial}
          </span>
        ) : null}

        {row.imei ? (
          <span className={styles.monoMeta}>
            {row.imei}
          </span>
        ) : null}
      </div>
    </div>
  );
};

const OperatorAvatar = ({ row, gui }) => {
  const src =
    can(gui, "READ_PERSON_PHOTO")
      ? operatorImageSrc(row)
      : "";

  if (src) {
    return (
      <span className={styles.operatorAvatar}>
        <img
          className={
            styles.operatorAvatarImage
          }
          src={src}
          alt={operatorName(row)}
        />
      </span>
    );
  }

  return (
    <span
      className={
        `${styles.operatorAvatar} ` +
        styles.operatorAvatarFallback
      }
    >
      <AccountCircleOutlinedIcon
        fontSize="medium"
      />
    </span>
  );
};

const OperatorCell = ({
  row,
  gui
}) => {
  const name =
    operatorName(row);

  if (!name) {
    return (
      <span className={styles.noOperator}>
        {t(
          "reporting.tabletNoOperator"
        )}
      </span>
    );
  }

  const accountStatus =
    accountStatusLabel(
      row.operatorAccountStatus
    );

  const disabled =
    String(
      row.operatorAccountStatus || ""
    )
      .trim()
      .toUpperCase() === "DISABLED";

  const content = (
    <>
      <OperatorAvatar row={row} gui={gui} />

      <span className={styles.operatorBody}>
        <span className={styles.operatorName}>
          {name}
        </span>

        <span className={styles.operatorMeta}>
          {row.operatorNationalId ? (
            <span className={styles.operatorNinu}>
              {row.operatorNationalId}
            </span>
          ) : null}

          {row.operatorUsername ? (
            <span>
              @{row.operatorUsername}
            </span>
          ) : null}
        </span>

        {accountStatus ? (
          <span
            className={
              disabled
                ? `${styles.accountState} ${styles.accountStateDisabled}`
                : styles.accountState
            }
          >
            <span className={styles.stateDot} />
            {accountStatus}
          </span>
        ) : null}
      </span>
    </>
  );

  if (row.operatorNationalId) {
    return (
      <button
        type="button"
        className={styles.operatorTile}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          gui.goTo(
            `/reporting/operators/${encodeURIComponent(row.operatorNationalId)}`
          );
        }}
      >
        {content}
      </button>
    );
  }

  if (row.operatorAccountGid && can(gui, "READ_USER_ACCOUNT")) {
    return (
      <button
        type="button"
        className={styles.operatorTile}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          gui.goTo(`/user-account/${row.operatorAccountGid}`);
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={styles.operatorTile}>
      {content}
    </div>
  );
};

const VotingCenterCell = ({
  row,
  gui
}) => {
  const code =
    String(
      row.votingCenterCode || ""
    ).trim();

  const name =
    String(
      row.votingCenterName || ""
    ).trim();

  if (!code && !name) {
    return (
      <span className={styles.emptyValue}>
        {t("reporting.noCenter")}
      </span>
    );
  }

  const content = (
    <span className={styles.centerCell}>
      {code ? (
        <span className={styles.centerCode}>
          {code}
        </span>
      ) : null}

      {name ? (
        <span className={styles.centerName}>
          {name}
        </span>
      ) : null}
    </span>
  );

  return content;
};

const VersionCell = ({ row }) => {
  const installed =
    String(
      row.vrAppVersion || ""
    ).trim();

  const required =
    String(
      row.vrRequiredVersion || ""
    ).trim();

  if (!installed) {
    return (
      <div className={styles.versionCell}>
        <span className={styles.versionStateError}>
          <span className={styles.stateDot} />
          {t(
            "reporting.tabletAppMissingShort"
          )}
        </span>

        {required ? (
          <span className={styles.secondaryText}>
            {t(
              "reporting.tabletRequiredVersion"
            )}: {required}
          </span>
        ) : null}
      </div>
    );
  }

  const current =
    !required ||
    installed === required;

  return (
    <div className={styles.versionCell}>
      <span className={styles.versionNumber}>
        {installed}
      </span>

      <span
        className={
          current
            ? styles.versionStateOk
            : styles.versionStateWarning
        }
      >
        <span className={styles.stateDot} />

        {current
          ? t(
              "reporting.tabletCurrent"
            )
          : t(
              "reporting.tabletOutdatedShort"
            )}
      </span>

      {!current ? (
        <span className={styles.secondaryText}>
          {t(
            "reporting.tabletRequiredVersion"
          )}: {required}
        </span>
      ) : null}
    </div>
  );
};

const BatteryCell = ({ row }) => {
  if (
    row.batteryLevel === null ||
    row.batteryLevel === undefined
  ) {
    return null;
  }

  const level =
    Math.max(
      0,
      Math.min(
        100,
        Number(row.batteryLevel)
      )
    );

  const levelClass =
    level < 20
      ? styles.batteryLow
      : level < 50
        ? styles.batteryMedium
        : styles.batteryHigh;

  return (
    <div className={styles.batteryCell}>
      <span
        className={
          `${styles.batteryIcon} ${levelClass}`
        }
      >
        <span
          className={styles.batteryFill}
          style={{
            width: `${level}%`
          }}
        />
        <span
          className={styles.batteryTip}
        />
      </span>

      <span className={styles.batteryValue}>
        {level}%
      </span>
    </div>
  );
};

const LastContactCell = ({ row }) => {
  const source =
    String(
      row.lastContactSource || ""
    ).toUpperCase();

  return (
    <div className={styles.contactCell}>
      <span className={styles.contactTime}>
        {formatDateTime(
          row.lastContactAt
        ) || "—"}
      </span>

      {source ? (
        <span className={styles.contactSource}>
          {source === "BACKEND"
            ? t(
                "reporting.tabletContactBackend"
              )
            : t(
                "reporting.tabletContactMdm"
              )}
        </span>
      ) : null}
    </div>
  );
};

const issuePresentation = (issue) => {
  switch (
    String(issue || "")
      .trim()
      .toUpperCase()
  ) {
  case "STALE":
    return {
      label:
        t(
          "reporting.tabletStaleShort"
        ),
      Icon:
        WarningAmberIcon,
      className:
        styles.reportStateWarning
    };

  case "OUTDATED":
    return {
      label:
        t(
          "reporting.tabletOutdatedShort"
        ),
      Icon:
        SystemUpdateAltIcon,
      className:
        styles.reportStateWarning
    };

  case "APP_MISSING":
    return {
      label:
        t(
          "reporting.tabletAppMissingShort"
        ),
      Icon:
        ErrorOutlineIcon,
      className:
        styles.reportStateError
    };

  default:
    return null;
  }
};

const StateLine = ({
  Icon,
  label,
  className
}) => (
  <span
    className={
      `${styles.reportStateLine} ${className}`
    }
  >
    <Icon
      className={styles.reportStateIcon}
      fontSize="inherit"
    />

    <span>{label}</span>
  </span>
);

const StateCell = ({ row }) => {
  const issues =
    Array.isArray(row.issues)
      ? row.issues
          .map(issuePresentation)
          .filter(Boolean)
      : [];

  if (!issues.length) {
    return (
      <StateLine
        Icon={CheckCircleOutlineIcon}
        label={t(
          "reporting.tabletOperational"
        )}
        className={
          styles.reportStateOk
        }
      />
    );
  }

  return (
    <div className={styles.reportStateList}>
      {issues.map(
        ({
          Icon,
          label,
          className
        }) => (
          <StateLine
            key={label}
            Icon={Icon}
            label={label}
            className={className}
          />
        )
      )}
    </div>
  );
};

const tabletReportsProjection = {
  storageKey:
    "haiti.tablet-reports",

  facets:
    TABLET_REPORTS_FACETS,

  beforeNavigationControl: ({ facetController }) => (
    <div className={styles.navigationControls}>
      {TABLET_REPORTS_FACETS.renderButton(facetController)}
      <ReportExcelExportButton
        endpoint="reporting/tablet-reports/export"
        fallbackFilename="tablet-inventory.xlsx"
        errorMessageKey="reporting.tabletExportFailed"
        getPayload={() => ({
          ...currentExportState,
          locale: getLocale()
        })}
      />
    </div>
  ),

  columns: [
    {
      title:
        "reporting.tablet",

      field:
        "mdmNumber",

      sortSelector:
        "tablet",

      render:
        (row) => (
          <TabletCell row={row} />
        )
    },

    {
      title:
        "voter.operator",

      field:
        "operatorName",

      sortSelector:
        "operator",

      render:
        (row, gui) => (
          <OperatorCell
            row={row}
            gui={gui}
          />
        )
    },

    {
      title:
        "voter.votingCenter",

      field:
        "votingCenterCode",

      sortSelector:
        "votingCenter",

      render:
        (row, gui) => (
          <VotingCenterCell
            row={row}
            gui={gui}
          />
        )
    },

    {
      title:
        "reporting.tabletVrApplication",

      field:
        "vrAppVersion",

      sortSelector:
        "vrAppVersion",

      render:
        (row) => (
          <VersionCell row={row} />
        )
    },

    {
      title:
        "reporting.tabletBattery",

      field:
        "batteryLevel",

      sortSelector:
        "batteryLevel",

      render:
        (row) => (
          <BatteryCell row={row} />
        )
    },

    {
      title:
        "reporting.tabletLastContact",

      field:
        "lastContactAt",

      sortSelector:
        "lastContactAt",

      filtering:
        false,

      render:
        (row) => (
          <LastContactCell
            row={row}
          />
        )
    },

    {
      title:
        "common.status",

      field:
        "issueCount",

      sortSelector:
        "issueCount",

      filtering:
        false,

      render:
        (row) => (
          <StateCell row={row} />
        )
    },

    {
      title:
        "common.actions",

      field:
        "__actions",

      sorting:
        false,

      filtering:
        false
    }
  ],

  actions: (row, gui) => (
    <>
      {row.operatorNationalId ? (
        <Tooltip
          title={t(
            "reporting.operatorReport"
          )}
        >
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              gui.goTo(
                `/reporting/operators/${encodeURIComponent(
                  row.operatorNationalId
                )}`
              );
            }}
          >
            <VisibilityIcon
              fontSize="small"
            />
          </IconButton>
        </Tooltip>
      ) : null}

      {row.operatorAccountGid && can(gui, "READ_USER_ACCOUNT") ? (
        <Tooltip
          title={t(
            "user.viewAccount"
          )}
        >
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              gui.goTo(
                `/user-account/${row.operatorAccountGid}`
              );
            }}
          >
            <BadgeOutlinedIcon
              fontSize="small"
            />
          </IconButton>
        </Tooltip>
      ) : null}
    </>
  ),

  fetch: async (query) => {
    const range =
      getEffectiveQuery(query);

    writeRangeToUrl({
      offset: range.offset,
      limit: range.limit
    });

    const sort =
      buildSortPayload(
        range.sort
      );

    currentExportState = {
      search: range.search,
      sort,
      filters: range.filters
    };

    const response =
      await rest.request(
        getServiceUri() +
          "reporting/tablet-reports/search",
        "POST",
        buildTabletReportPayload({
          search:
            range.search,

          filters:
            range.filters,

          offset:
            range.offset,

          limit:
            range.limit,

          sort
        })
      );

    return {
      data:
        Array.isArray(
          response?.items
        )
          ? response.items
          : [],

      totalCount:
        Number(
          response?.total || 0
        ),

      page:
        range.page
    };
  }
};

export const displayTabletReports = (
  gui
) => {
  initialRange =
    readRangeFromUrl();

  initialRangeUsesLeft =
    initialRange.hasExplicitRange
      ? 3
      : 0;

  return displayList(
    gui,
    tabletReportsProjection
  );
};

export default displayTabletReports;
